import { useState, useMemo } from "react";
import { ChevronRight, ChevronDown, CheckCircle, Clock, XCircle, ListTodo } from "lucide-react";

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function formatMonth(key) {
  const [y, m] = key.split("-");
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

function capitalize(s) {
  return s ? s.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()) : "Other";
}

const TILES = [
  { key: "confirmed", label: "Confirmed", icon: CheckCircle, statuses: ["CONFIRMED", "COMPLETED"], color: "bg-emerald-50 border-emerald-200 text-emerald-800", iconColor: "text-emerald-600", activeColor: "ring-2 ring-emerald-500" },
  { key: "pending", label: "Pending", icon: Clock, statuses: ["PENDING_PAYMENT", "PENDING_CASH"], color: "bg-amber-50 border-amber-200 text-amber-800", iconColor: "text-amber-500", activeColor: "ring-2 ring-amber-500" },
  { key: "cancelled", label: "Cancelled", icon: XCircle, statuses: ["CANCELLED", "EXPIRED"], color: "bg-red-50 border-red-200 text-red-800", iconColor: "text-red-500", activeColor: "ring-2 ring-red-500" },
  { key: "waitlist", label: "Waiting List", icon: ListTodo, statuses: [], color: "bg-blue-50 border-blue-200 text-blue-800", iconColor: "text-blue-500", activeColor: "ring-2 ring-blue-500" },
];

export function BookingsGroupedView({ bookings, renderItem, waitlistCount = 0, waitlistItems = [], renderWaitlistItem }) {
  const [activeTile, setActiveTile] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});
  const [expandedSports, setExpandedSports] = useState({});

  // Tile counts
  const tileCounts = useMemo(() => {
    const counts = {};
    TILES.forEach(t => {
      if (t.key === "waitlist") {
        counts[t.key] = waitlistCount;
      } else {
        counts[t.key] = bookings.filter(b => t.statuses.includes(b.status)).length;
      }
    });
    return counts;
  }, [bookings, waitlistCount]);

  // Apply tile filter
  const visibleBookings = useMemo(() => {
    if (!activeTile || activeTile === "waitlist") return bookings;
    const tile = TILES.find(t => t.key === activeTile);
    if (!tile) return bookings;
    return bookings.filter(b => tile.statuses.includes(b.status));
  }, [bookings, activeTile]);

  // Sort by most recent
  const sorted = useMemo(() =>
    [...visibleBookings].sort((a, b) => (b.start_dt || "").localeCompare(a.start_dt || "")),
    [visibleBookings]
  );

  // Group by month then sport
  const monthGroups = useMemo(() => {
    const groups = {};
    sorted.forEach(b => {
      const dt = b.start_dt || b.created_at || "";
      const monthKey = dt.slice(0, 7) || "unknown";
      if (!groups[monthKey]) groups[monthKey] = {};
      const sport = b.sport_type || "other";
      if (!groups[monthKey][sport]) groups[monthKey][sport] = [];
      groups[monthKey][sport].push(b);
    });
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([monthKey, sports]) => ({
        monthKey,
        monthLabel: monthKey === "unknown" ? "Unknown Date" : formatMonth(monthKey),
        totalCount: Object.values(sports).reduce((sum, arr) => sum + arr.length, 0),
        sports: Object.entries(sports)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([sport, items]) => ({ sport, label: capitalize(sport), items })),
      }));
  }, [sorted]);

  const toggleMonth = (key) => setExpandedMonths(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleSport = (key) => setExpandedSports(prev => ({ ...prev, [key]: !prev[key] }));

  const handleTileClick = (key) => {
    setActiveTile(prev => prev === key ? null : key);
    setExpandedMonths({});
    setExpandedSports({});
  };

  const showWaitlist = activeTile === "waitlist";

  return (
    <div className="space-y-4">
      {/* Summary Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bookings-summary-tiles">
        {TILES.map(tile => {
          const Icon = tile.icon;
          const isActive = activeTile === tile.key;
          return (
            <button
              key={tile.key}
              onClick={() => handleTileClick(tile.key)}
              className={`flex items-center gap-3 p-4 rounded-lg border transition-all cursor-pointer ${tile.color} ${isActive ? tile.activeColor : "hover:opacity-80"}`}
              data-testid={`tile-${tile.key}`}
            >
              <Icon className={`w-5 h-5 ${tile.iconColor} shrink-0`} />
              <div className="text-left min-w-0">
                <p className="text-2xl font-heading font-bold leading-none">{tileCounts[tile.key]}</p>
                <p className="text-[10px] font-heading uppercase tracking-wider mt-0.5">{tile.label}</p>
              </div>
            </button>
          );
        })}
      </div>

      {activeTile && (
        <p className="text-xs text-muted-foreground">
          Filtering by <span className="font-semibold capitalize">{activeTile === "waitlist" ? "Waiting List" : activeTile}</span>
          <button onClick={() => setActiveTile(null)} className="ml-2 text-primary underline text-xs" data-testid="clear-tile-filter">Clear filter</button>
        </p>
      )}

      {/* Waitlist view */}
      {showWaitlist ? (
        <div className="space-y-2" data-testid="waitlist-grouped-view">
          {waitlistItems.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg">
              <ListTodo className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="font-heading text-sm text-muted-foreground uppercase">No waiting list entries</p>
            </div>
          ) : (
            waitlistItems.map((item, i) => (
              <div key={item.waitlist_id || item.booking_id || i}>{renderWaitlistItem ? renderWaitlistItem(item) : renderItem(item)}</div>
            ))
          )}
        </div>
      ) : (
        /* Month → Sport grouped collapsible view */
        <div className="space-y-2" data-testid="bookings-grouped-view">
          {monthGroups.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-lg">
              <p className="font-heading text-sm text-muted-foreground uppercase">No bookings found</p>
            </div>
          ) : (
            monthGroups.map(({ monthKey, monthLabel, totalCount, sports }) => {
              const isMonthOpen = !!expandedMonths[monthKey];
              return (
                <div key={monthKey} className="border rounded-lg overflow-hidden" data-testid={`month-group-${monthKey}`}>
                  <button
                    onClick={() => toggleMonth(monthKey)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                    data-testid={`month-toggle-${monthKey}`}
                  >
                    <div className="flex items-center gap-2">
                      {isMonthOpen ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
                      <span className="font-heading font-bold text-sm uppercase tracking-wide">{monthLabel}</span>
                    </div>
                    <span className="text-xs text-muted-foreground font-heading">{totalCount} booking{totalCount !== 1 ? "s" : ""}</span>
                  </button>
                  {isMonthOpen && (
                    <div className="px-3 py-2 space-y-2">
                      {sports.map(({ sport, label, items }) => {
                        const sportKey = `${monthKey}_${sport}`;
                        const isSportOpen = !!expandedSports[sportKey];
                        return (
                          <div key={sportKey} data-testid={`sport-group-${sportKey}`}>
                            <button
                              onClick={() => toggleSport(sportKey)}
                              className="w-full flex items-center justify-between px-3 py-2 rounded-md hover:bg-slate-50 transition-colors"
                              data-testid={`sport-toggle-${sportKey}`}
                            >
                              <div className="flex items-center gap-2">
                                {isSportOpen ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
                                <span className="text-sm font-medium capitalize">{label}</span>
                              </div>
                              <span className="text-[10px] text-muted-foreground">{items.length}</span>
                            </button>
                            {isSportOpen && (
                              <div className="pl-6 pr-1 pb-2 space-y-2">
                                {items.map((b, i) => (
                                  <div key={b.booking_id || i}>{renderItem(b)}</div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
